import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import {
  X,
  Plus,
  Trash2,
  FileText,
  Search,
  Check,
  Globe,
  Star,
  ExternalLink,
} from 'lucide-react';

interface PageManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PageManagerModal: React.FC<PageManagerModalProps> = ({ isOpen, onClose }) => {
  const {
    activeSite,
    activePageId,
    setActivePageId,
    addPageToSite,
    updatePageInSite,
    deletePageFromSite,
  } = useSite();

  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newSeoTitle, setNewSeoTitle] = useState('');
  const [newSeoDescription, setNewSeoDescription] = useState('');

  if (!isOpen || !activeSite) return null;

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle) return;
    const slug = newSlug || newTitle.toLowerCase().replace(/[^a-z0-9]/g, '-');
    addPageToSite(newTitle, slug, newSeoTitle, newSeoDescription);
    setIsCreating(false);
    setNewTitle('');
    setNewSlug('');
    setNewSeoTitle('');
    setNewSeoDescription('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Sayfa Yönetimi</h3>
              <p className="text-xs text-slate-400">Sitenizin tüm sayfalarını ve SEO ayarlarını düzenleyin.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* Create Page Form Toggle */}
          {isCreating ? (
            <form onSubmit={handleCreate} className="p-4 bg-slate-800/80 rounded-xl border border-slate-700 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-emerald-400">Yeni Sayfa Oluştur</span>
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="text-xs text-slate-400 hover:text-slate-200"
                >
                  İptal
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Sayfa Başlığı</label>
                <input
                  required
                  type="text"
                  placeholder="Örn: Hizmetlerimiz veya Referanslar"
                  value={newTitle}
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                    if (!newSlug) {
                      setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-'));
                    }
                  }}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Sayfa URL (Slug)</label>
                <div className="flex items-center bg-slate-900 border border-slate-700 rounded-lg overflow-hidden text-xs text-slate-400 px-3">
                  <span>/</span>
                  <input
                    required
                    type="text"
                    placeholder="hizmetlerimiz"
                    value={newSlug.replace(/^\//, '')}
                    onChange={(e) => setNewSlug(`/${e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')}`)}
                    className="w-full py-2 px-1 bg-transparent text-slate-200 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">SEO Başlığı (Meta Title)</label>
                <input
                  type="text"
                  placeholder="Arama motorlarında görünecek başlık"
                  value={newSeoTitle}
                  onChange={(e) => setNewSeoTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-slate-200 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">SEO Açıklaması (Meta Description)</label>
                <textarea
                  rows={2}
                  placeholder="Sayfa hakkında kısa özet..."
                  value={newSeoDescription}
                  onChange={(e) => setNewSeoDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-slate-200 outline-none"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg shadow transition"
                >
                  Sayfayı Oluştur ve Düzenle
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full py-2.5 rounded-xl border border-dashed border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800/40 text-emerald-400 text-xs font-bold transition flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Yeni Sayfa Ekle
            </button>
          )}

          {/* Current Page List */}
          <div className="space-y-2.5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Mevcut Sayfalar ({(activeSite.pages || []).length})
            </div>

            {(activeSite.pages || []).map((page) => {
              const isActive = activePageId === page.id;
              return (
                <div
                  key={page.id}
                  className={`p-3.5 rounded-xl border transition flex items-center justify-between ${
                    isActive
                      ? 'bg-blue-600/20 border-blue-500/80 text-blue-200'
                      : 'bg-slate-800/80 border-slate-700/80 text-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-slate-300 text-xs font-bold">
                      {page.isHomePage ? <Star className="w-4 h-4 text-amber-400 fill-amber-400" /> : <FileText className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="font-bold text-xs text-slate-100 flex items-center gap-2">
                        {page.title}
                        {page.isHomePage && (
                          <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-semibold">
                            Ana Sayfa
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400">{page.slug}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setActivePageId(page.id);
                        onClose();
                      }}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow transition"
                    >
                      Düzenle
                    </button>
                    {!page.isHomePage && (
                      <button
                        onClick={() => deletePageFromSite(page.id)}
                        className="p-1.5 rounded-lg bg-slate-700 hover:bg-rose-900/50 text-slate-400 hover:text-rose-300 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
